import type { iMap } from '../components/type';

/** Summary row from GET /collections/recent */
export type CollectCollectionLinkSummary = {
    id: string;
    name: string;
    company_id?: string | null;
    map_ids?: string[];
    linked_process_id?: string | null;
    /** At least one non-empty contributor answer (synthesis / response-matrix). */
    has_responses?: boolean;
    /** True when the row is visible only because the user is an invited contributor. */
    opened_as_contributor?: boolean;
    is_primary_owner?: boolean;
    is_collection_co_owner?: boolean;
};

/** Same active company as Insights session (carto ↔ collecte linking). */
export function collectionSummaryInCompany(
    summary: { company_id?: string | null },
    companyId: string | null | undefined,
): boolean {
    if (!companyId) return true;
    if (!summary.company_id) return true;
    return String(summary.company_id) === String(companyId);
}

/** Owner or co-owner only — excludes contributor rows (Insights scope modal). */
export function collectionSummaryManageableByOwner(summary: CollectCollectionLinkSummary): boolean {
    if (summary.opened_as_contributor === true) return false;
    if (summary.is_primary_owner === true || summary.is_collection_co_owner === true) return true;
    return (
        summary.is_primary_owner === undefined
        && summary.is_collection_co_owner === undefined
    );
}

/** Owner, co-owner, or invited contributor — may use collect data in Insights. */
export function collectionSummaryUsableInInsights(summary: CollectCollectionLinkSummary): boolean {
    if (summary.is_primary_owner === true || summary.is_collection_co_owner === true) return true;
    if (summary.opened_as_contributor === true) return true;
    return (
        summary.is_primary_owner === undefined
        && summary.is_collection_co_owner === undefined
        && summary.opened_as_contributor === undefined
    );
}

/** @deprecated Use collectionSummaryUsableInInsights */
export const collectionSummaryManageableForInsights = collectionSummaryUsableInInsights;

export type SelectedCollectCollection = {
    id: string;
    name: string;
    map_ids: string[];
    linked_process_id?: string | null;
};

export function mapIdsFromCollections(collections: ReadonlyArray<{ map_ids?: string[] }>): string[] {
    const ids = new Set<string>();
    for (const c of collections) {
        for (const mid of c.map_ids || []) {
            if (mid) ids.add(mid);
        }
    }
    return [...ids];
}

export function collectionsLinkedToMapIds(
    collections: ReadonlyArray<CollectCollectionLinkSummary>,
    mapIds: string[],
    maps: ReadonlyArray<iMap> = [],
): CollectCollectionLinkSummary[] {
    if (!mapIds.length) return [];
    const mapIdSet = new Set(mapIds);
    const processByMapId = new Map<string, string>();
    for (const m of maps) {
        const mid = m._id;
        const pid = typeof m.process === 'object' && m.process !== null ? m.process._id : undefined;
        if (mid && pid) processByMapId.set(mid, pid);
    }

    return collections.filter((c) => {
        if ((c.map_ids || []).some((id) => mapIdSet.has(id))) return true;
        const linkedPid = c.linked_process_id;
        if (!linkedPid) return false;
        return [...processByMapId.entries()].some(
            ([mid, pid]) => mapIdSet.has(mid) && pid === linkedPid,
        );
    });
}

/** Build linked_map_ids for a collection against the current map selection (local fallback). */
export function linkedMapIdsForCollection(
    collection: CollectCollectionLinkSummary,
    mapIds: string[],
    maps: ReadonlyArray<iMap> = [],
): string[] {
    const mapIdSet = new Set(mapIds);
    const linked: string[] = [];
    for (const mid of collection.map_ids || []) {
        if (mapIdSet.has(mid) && !linked.includes(mid)) linked.push(mid);
    }
    const linkedPid = collection.linked_process_id;
    if (linkedPid) {
        for (const m of maps) {
            const mid = m._id;
            const pid = typeof m.process === 'object' && m.process !== null ? m.process._id : undefined;
            if (mid && mapIdSet.has(mid) && pid === linkedPid && !linked.includes(mid)) {
                linked.push(mid);
            }
        }
    }
    return linked;
}

export function mergeCollectionSelections(
    current: SelectedCollectCollection[],
    toAdd: CollectCollectionLinkSummary[],
): SelectedCollectCollection[] {
    const byId = new Map(current.map((c) => [c.id, c]));
    for (const c of toAdd) {
        if (!byId.has(c.id)) {
            byId.set(c.id, {
                id: c.id,
                name: c.name,
                map_ids: [...(c.map_ids || [])],
                linked_process_id: c.linked_process_id ?? null,
            });
        }
    }
    return [...byId.values()];
}

export function mergeMapsById(current: iMap[], toAdd: iMap[]): iMap[] {
    const byId = new Map(current.map((m) => [m._id, m]));
    for (const m of toAdd) {
        if (m?._id && !byId.has(m._id)) byId.set(m._id, m);
    }
    return [...byId.values()];
}

/** True when at least one contributor cell has a non-empty answer. */
export function collectMatrixHasContributorResponses(
    matrix: { rows?: ReadonlyArray<{ answers?: Record<string, string> }> } | null | undefined,
): boolean {
    if (!matrix?.rows?.length) return false;
    for (const row of matrix.rows) {
        for (const text of Object.values(row.answers || {})) {
            if (String(text || '').trim()) return true;
        }
    }
    return false;
}

export function collectionSummaryHasResponses(summary: { has_responses?: boolean }): boolean {
    return summary.has_responses === true;
}

/** Latest map for a process when collection has linked_process_id but no map_ids. */
export function resolveMapIdsForProcess(allMaps: iMap[], processId: string | null | undefined): string[] {
    if (!processId) return [];
    const matches = allMaps.filter((m) => m.process?._id === processId);
    if (!matches.length) return [];
    const sorted = [...matches].sort((a, b) => {
        const ta = a.created_at ? Date.parse(a.created_at) || 0 : 0;
        const tb = b.created_at ? Date.parse(b.created_at) || 0 : 0;
        if (tb !== ta) return tb - ta;
        return (b.number ?? 0) - (a.number ?? 0);
    });
    return sorted[0]?._id ? [sorted[0]._id] : [];
}

export async function resolveMapIdsForCollection(
    collection: SelectedCollectCollection,
    allMaps: iMap[],
): Promise<string[]> {
    const direct = (collection.map_ids || []).filter(Boolean);
    if (direct.length) return direct;
    return resolveMapIdsForProcess(allMaps, collection.linked_process_id);
}

type ScopeCollectionRow = {
    id: string;
    name: string;
    map_ids?: string[];
    linked_process_id?: string | null;
};

/** Restaure les collectes à partir du scope API (+ fetch direct par id si besoin). */
export async function resolveCollectionsForAnalysisScope(
    scope: {
        collections?: ScopeCollectionRow[];
        collection_ids?: string[];
    } | null | undefined,
    accessToken: string,
    reportCollectionIds: string[] = [],
): Promise<SelectedCollectCollection[]> {
    const idSet = new Set<string>([
        ...(scope?.collection_ids || []),
        ...reportCollectionIds,
    ]);
    if (!idSet.size) return [];

    const byId = new Map<string, SelectedCollectCollection>();
    for (const c of scope?.collections || []) {
        if (!c?.id) continue;
        byId.set(c.id, {
            id: c.id,
            name: c.name,
            map_ids: [...(c.map_ids || [])],
            linked_process_id: c.linked_process_id ?? null,
        });
    }

    const missing = [...idSet].filter((id) => !byId.has(id));
    if (missing.length && accessToken) {
        const { getCollectDataCollection } = await import('../services/api/collectDataService');
        await Promise.all(
            missing.map(async (id) => {
                try {
                    const res = await getCollectDataCollection(id, accessToken);
                    const d = res.status === 200 ? res.body?.data?.collection : null;
                    if (!d?.id && !d?.name) return;
                    byId.set(id, {
                        id: d.id ?? id,
                        name: d.name ?? id,
                        map_ids: [...(d.map_ids || [])],
                        linked_process_id: d.linked_process_id ?? null,
                    });
                } catch {
                    // collecte inaccessible — ignorée
                }
            }),
        );
    }

    return [...idSet].map((id) => byId.get(id)).filter(Boolean) as SelectedCollectCollection[];
}
